import { useMemo } from 'react';
import styled from 'styled-components';

import DeleteIcon from '#/assets/DeleteIcon';
import copyToClipboard from '#/utils/copyToClipboard';
import maskString from '#/utils/maskString';

import { useApi } from '#/api/fetch-api';
import { cancelOrder, Order } from '#/api/orders';

import { cA568FE, cCC38FF } from '#/colors';

import toast from '#/components/common/toast';
import { Loadable } from '#/components/shared/Loadable';

import { useOpenOrdersView } from '#/features/perpetuals/trade-overview/open-orders-context';
import { useTranslation } from '#/features/localization/utils';

import { createTradeOverviewTable, TableSpec } from './table';
import { Empty, Error, Mono, SideBuy, SideSell } from './atoms';

interface Props {
  tableSpec: TableSpec<string>;
}

export default function OpenOrders(props: Props) {
  const { tableSpec } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, error, openOrders } = useOpenOrdersView();
  const { t } = useTranslation();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = openOrders.length === 0;
  const isLoading = status === 'loading' || status === 'disconnected';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>{t('You have no open orders')}</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {openOrders.map((order) => (
          <Row
            key={order.id}
            time={
              <span css="font-size: 11px;">
                <span>{order.createdTime}</span>
                <br />
                <span css="opacity: 0.7">{order.createdDate}</span>
              </span>
            }
            market={order.market}
            type={order.type}
            instruction={order.instruction}
            side={sideComponent[order.side]}
            price={<Mono>{order.price}</Mono>}
            amount={<Mono>{order.size}</Mono>}
            filled={<Mono>{order.filledSize}</Mono>}
            actions={
              <span>
                <CancelOrder id={order.id} />
              </span>
            }
            clientId={
              <Id
                title={order.clientId}
                onClick={() => copyToClipboard(order.clientId)}
              >
                {maskString(order.clientId, 6, 6)}
              </Id>
            }
          />
        ))}
      </Table>
    </Loadable>
  );
}

function CancelOrder({ id }: { id: Order['id'] }) {
  const [{ isLoading }, exec] = useApi(cancelOrder);

  const handleCancelOrder = () => {
    exec({ id }, (resp) => {
      if (!resp.ok) {
        toast.error(resp.message);
      }
    });
  };

  return (
    <Cancel disabled={isLoading} onClick={handleCancelOrder}>
      <DeleteIcon />
    </Cancel>
  );
}

const sideComponent = {
  BUY: <SideBuy />,
  SELL: <SideSell />,
};

const Cancel = styled.button.attrs({
  'aria-label': 'Cancel order',
  title: 'Cancel order',
})`
  display: grid;
  align-content: center;
  border: none;
  background: none;
  color: ${cCC38FF};

  &[disabled] {
    opacity: 0.5;
  }
`;

const Id = styled.div`
  &:hover {
    cursor: pointer;
    color: ${cA568FE};
  }
`;
